import React, { useState } from "react";

/**
 * Form field primitives (§2.7): label above, help text under, inline error.
 * <Field> wraps any control; <Input>/<Select>/<PasswordInput> are the styled
 * controls. Always pass real `autoComplete` attributes on identity fields —
 * the missing-autocomplete autofill hazard is on the audit's kill list.
 */

export function Field({ label, help, error, required, htmlFor, children }) {
  return (
    <label className="fk-field" htmlFor={htmlFor}>
      {label ? (
        <span className="fk-field__label">
          {label}
          {required ? <span className="fk-field__required">*</span> : null}
        </span>
      ) : null}
      {children}
      {error ? (
        <span className="fk-field__error" role="alert">
          {error}
        </span>
      ) : help ? (
        <span className="fk-field__help">{help}</span>
      ) : null}
    </label>
  );
}

export function Input({ error, className = "", ...rest }) {
  const classes = ["fk-input", error ? "fk-input--error" : "", className].filter(Boolean).join(" ");
  return <input className={classes} {...rest} />;
}

export function TextArea({ error, className = "", ...rest }) {
  const classes = ["fk-input", error ? "fk-input--error" : "", className].filter(Boolean).join(" ");
  return <textarea className={classes} {...rest} />;
}

export function Select({ error, className = "", children, ...rest }) {
  const classes = ["fk-select", error ? "fk-select--error" : "", className]
    .filter(Boolean)
    .join(" ");
  return (
    <select className={classes} {...rest}>
      {children}
    </select>
  );
}

/** Password input with a show/hide toggle. */
export function PasswordInput({ error, ...rest }) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="fk-input-group">
      <Input type={visible ? "text" : "password"} error={error} {...rest} />
      <button
        type="button"
        className="fk-input-group__suffix"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
      >
        {visible ? "Hide" : "Show"}
      </button>
    </span>
  );
}
